import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import DialogV2 from 'common/modal/DialogV2';
import Notifier from 'common/helpers/Notifier';
import { COMMUNITY_ENGAGEMENT_INTERACTION_CODE, ENTERED_IN_ERROR_STATE_CODE } from '../constants';
import { useCurrentEmployee } from '../context/InteractionsContext';
import { useDeleteInteraction } from '../hooks/useDeleteInteraction';
import { useUpdateProcedure } from '../hooks/useUpdateProcedure';
import { useValueSet } from '../hooks/useValueSet';
import { CommunityEngagementInteractionCardBody } from './CommunityEngagementInteractionCardBody';
import { InteractionCardHeader } from './InteractionCardHeader';
import { InteractionDeleteModal } from './InteractionDeleteModal';

export function StructuredInteractionCard({
  interaction,
  onEdit,
}) {
  const typeCode = interaction.type_concept.code;
  const typeLabel = interaction.type_concept.display;
  const isCommunityEngagementInteraction = typeCode === COMMUNITY_ENGAGEMENT_INTERACTION_CODE;
  const deleteInteractionDialogRef = useRef(null);
  const currentEmployee = useCurrentEmployee();

  const { updateRecord: deleteInteraction } = useDeleteInteraction();
  const { updateRecord: updateProcedure } = useUpdateProcedure();
  const { conceptId: enteredInErrorConceptId, isLoading } = useValueSet(ENTERED_IN_ERROR_STATE_CODE);
  const deleteDisabled = isLoading || !enteredInErrorConceptId;

  const openDeleteDialog = (event) => {
    event.stopPropagation();
    if (deleteInteractionDialogRef.current) {
      deleteInteractionDialogRef.current.openDialog();
    }
  };

  const cancelDelete = () => {
    if (deleteInteractionDialogRef.current) {
      deleteInteractionDialogRef.current.closeDialog();
    }
  };

  const confirmDelete = async () => {
    cancelDelete();

    const softDeletePayload = {
      state_concept: enteredInErrorConceptId,
      recorded_by: currentEmployee?.id,
    };
    const procedureId = interaction.procedures?.[0]?.id;

    try {
      await deleteInteraction(interaction.id, softDeletePayload);
      await updateProcedure(procedureId, softDeletePayload);

      Notifier.dispatch('success', 'Interaction deleted.');
    } catch (error) {
      Notifier.handleErrors(error);
    }
  };

  return (
    <div
      className="mb-4 overflow-hidden rounded border border-solid border-dark-border-blue"
      data-testid={`structured-interaction-card-${interaction.id}`}
    >
      <div className="bg-medium-fill-grey px-4 pt-4">
        <InteractionCardHeader
          title={typeLabel}
          updatedAt={interaction.updated_at}
          interactionId={interaction.id}
          onEdit={onEdit}
          onDelete={openDeleteDialog}
          deleteDisabled={deleteDisabled}
        />
      </div>
      {isCommunityEngagementInteraction && (
        <div className="bg-white px-4 py-4">
          <CommunityEngagementInteractionCardBody interaction={interaction} />
        </div>
      )}
      <DialogV2
        ref={deleteInteractionDialogRef}
        title="Delete Interaction"
        confirmLabel="Delete"
        cancelHandler={cancelDelete}
        confirmationHandler={confirmDelete}
        dataTestId="structured-interaction-delete-dialog"
        confirmButtonTestId="structured-interaction-delete-confirm"
      >
        <InteractionDeleteModal />
      </DialogV2>
    </div>
  );
}

StructuredInteractionCard.propTypes = {
  interaction: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};
